import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, ArrowDownRight, Send, TrendingUp, CreditCard, Banknote } from 'lucide-react';
import { transactionsAPI } from '../services/api';
import { formatCurrency, formatDate } from '../utils/constants';
import { toast } from 'sonner';
import Layout from '../components/Layout';

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTransactions();
  }, []);

  const loadTransactions = async () => {
    try {
      const response = await transactionsAPI.getHistory();
      setTransactions(response.data);
    } catch (error) {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'deposit':
        return { Icon: ArrowDownRight, color: 'text-[#00FF94]', bg: 'bg-[#00FF94]/20' };
      case 'transfer':
        return { Icon: Send, color: 'text-[#00F0FF]', bg: 'bg-[#00F0FF]/20' };
      case 'convert':
        return { Icon: TrendingUp, color: 'text-[#FFD700]', bg: 'bg-[#FFD700]/20' };
      case 'card_purchase':
        return { Icon: CreditCard, color: 'text-[#FF0080]', bg: 'bg-[#FF0080]/20' };
      case 'bank_transfer':
        return { Icon: Banknote, color: 'text-[#7000FF]', bg: 'bg-[#7000FF]/20' };
      default:
        return { Icon: ArrowUpRight, color: 'text-gray-400', bg: 'bg-gray-400/20' };
    }
  };

  const getTransactionLabel = (tx) => {
    switch (tx.type) {
      case 'deposit':
        return `Deposit ${tx.crypto}`;
      case 'transfer':
        return `Send ${tx.crypto}`;
      case 'convert':
        return `Convert ${tx.crypto} to AED`;
      case 'card_purchase':
        return `Purchase ${tx.details.tier} Card`;
      case 'bank_transfer':
        return 'Bank Transfer';
      default:
        return tx.type;
    }
  };

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-primary font-bold text-white mb-2">TRANSACTION HISTORY</h1>
          <p className="text-gray-400 font-secondary">View all your transactions</p>
        </motion.div>

        {/* Transactions List */}
        <div className="glass-card rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-gray-400">Loading...</div>
          ) : transactions.length === 0 ? (
            <div className="p-12 text-center text-gray-400 font-secondary">No transactions yet</div>
          ) : (
            <div className="divide-y divide-white/10">
              {transactions.map((tx, index) => {
                const { Icon, color, bg } = getTransactionIcon(tx.type);
                return (
                  <motion.div
                    key={tx.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="p-6 hover:bg-white/5 transition-colors duration-300"
                    data-testid={`transaction-${tx.type}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <div className={`w-12 h-12 rounded-full ${bg} flex items-center justify-center flex-shrink-0`}>
                          <Icon className={`w-6 h-6 ${color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-white font-secondary font-semibold text-lg mb-1">
                            {getTransactionLabel(tx)}
                          </div>
                          <div className="text-gray-400 text-sm font-secondary">
                            {formatDate(tx.timestamp)}
                          </div>
                          {tx.details && Object.keys(tx.details).length > 0 && (
                            <div className="text-gray-500 text-xs mt-1 font-mono">
                              {tx.details.to_address && `To: ${tx.details.to_address.slice(0, 10)}...${tx.details.to_address.slice(-8)}`}
                              {tx.details.network && ` • ${tx.details.network}`}
                              {tx.details.converted_amount && ` • Received ${formatCurrency(tx.details.converted_amount)}`}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right ml-4">
                        <div className="text-white font-mono text-lg font-bold">
                          {tx.crypto ? `${tx.amount} ${tx.crypto}` : formatCurrency(tx.amount)}
                        </div>
                        <div className={`text-sm mt-1 ${
                          tx.status === 'completed' ? 'text-[#00FF94]' :
                          tx.status === 'pending' ? 'text-[#FFD700]' :
                          tx.status === 'failed' ? 'text-[#FF0080]' :
                          'text-gray-400'
                        }`}>
                          {tx.status.charAt(0).toUpperCase() + tx.status.slice(1)}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Transactions;
